/**
 * useRiderData Hook
 * 라이더 정산 목록 조회 및 출금제한 관리 훅
 *
 * @description
 * - 라이더 목록 조회 (협력사/본사 권한별 자동 필터링)
 * - 검색 조건 관리 (라이더명, 라이더ID, 계약상태, 협력사)
 * - 클라이언트 사이드 페이지네이션
 * - 출금제한 상태 변경
 *
 * @returns 라이더 데이터 상태 및 관리 함수
 */
import {useCallback, useEffect, useState} from 'react';
import {riderAccountingApi} from '../api/RiderAccountingApi';
import {partnerAccountingApi} from "../api/PartnerAccountingApi";
import {ContractStatusOption, RiderListItem} from '../types/riderAccounting.types';
import {PartnerNameItem} from "../types/AccountingPartner.types";
import {useToast} from '@/shared/components/feedback/Toast/ToastContext';
import {ToastType} from '@/shared/components/feedback/Toast/Toast.types';
import {getItem} from '@/shared/utils/storage';
import {WITHDRAWAL_STATUS, WITHDRAWAL_STATUS_LABELS} from '@/shared/constants/statusCodes';

const ITEMS_PER_PAGE = { DEFAULT: 10 };

export const useRiderData = () => {
    const { showToast } = useToast();

    // 사용자 타입과 ID 가져오기
    const userType = getItem<string>('USER_TYPE');
    const userId = getItem<string>('USER_ID');

    const [searchRiderName, setSearchRiderName] = useState('');
    const [searchRiderId, setSearchRiderId] = useState('');
    const [searchContractStatus, setSearchContractStatus] = useState('');
    const [searchPartnerId, setSearchPartnerId] = useState('');
    const [searchWithdrawalStatus, setSearchWithdrawalStatus] = useState(''); // 출금상태 필터 추가
    const [allData, setAllData] = useState<RiderListItem[]>([]);
    const [riderData, setRiderData] = useState<RiderListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [contractStatusCodes, setContractStatusCodes] = useState<ContractStatusOption[]>([]);
    const [contractStatusLoading, setContractStatusLoading] = useState(false);
    const [partnerNames, setPartnerNames] = useState<PartnerNameItem[]>([]);
    const itemsPerPage = ITEMS_PER_PAGE.DEFAULT;

    // 페이지 데이터 업데이트 (클라이언트 사이드 페이지네이션)
    const updatePageData = useCallback((data: RiderListItem[], page: number) => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageData = data.slice(startIndex, endIndex);

        setRiderData(pageData);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
        setCurrentPage(page);
    }, [itemsPerPage]);

    // 라이더 목록 조회
    const fetchRiderList = useCallback(async () => {
        try {
            setLoading(true);

            let response;

            // 협력사 사용자일 때는 협력사 ID로 조회
            if (userType === 'SUPPLIER' && userId) {
                // 빈 값 제거 및 필드명 매핑
                const params: Record<string, string> = {};
                params.suppId = userId;

                response = await riderAccountingApi.searchRiders(params);
            } else {
                // 관리자는 전체 데이터 조회
                response = await riderAccountingApi.getRiderList();
            }

            if (Array.isArray(response)) {
                setAllData(response);
                updatePageData(response, 1);
            } else {
                setAllData([]);
                updatePageData([], 1);
            }
        } catch (error) {
            showToast('라이더 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
            updatePageData([], 1);
        } finally {
            setLoading(false);
        }
    }, [userType, userId, showToast, updatePageData]);

    // 검색 처리
    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setCurrentPage(1);

            // 협력사 사용자일 때는 자신의 협력사 ID로 자동 설정
            const partnerId = userType === 'SUPPLIER' && userId ? userId : searchPartnerId;

            // 빈 값 제거 및 필드명 매핑
            const params: Record<string, string> = {};
            if (partnerId) params.suppId = partnerId;
            if (searchRiderName) params.riderName = searchRiderName;
            if (searchRiderId) params.riderId = searchRiderId;
            if (searchContractStatus) params.contStatus = searchContractStatus;
            if (searchWithdrawalStatus) params.withdrawalYn = searchWithdrawalStatus; // 출금상태 필터 추가

            // 검색 API 호출
            const response = await riderAccountingApi.searchRiders(params);


            if (Array.isArray(response)) {
                setAllData(response);
                updatePageData(response, 1);
            } else {
                setAllData([]);
                updatePageData([], 1);
            }
        } catch (error) {
            showToast('라이더 검색에 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
            updatePageData([], 1);
        } finally {
            setLoading(false);
        }
    }, [userType, userId, searchPartnerId, searchRiderName, searchRiderId, searchContractStatus, searchWithdrawalStatus, showToast, updatePageData]);

    // 페이지 변경 처리
    const handlePageChange = useCallback((page: number) => {
        updatePageData(allData, page);
    }, [allData, updatePageData]);

    // 출금제한 처리
    const handleWithdrawalLimit = useCallback(async (riderName: string, riderId: string, suppId: string, currentStatus: string | null, targetAction: 'ALLOW' | 'RESTRICT') => {
        // 타겟 액션에 따라 새 상태와 액션 메시지 결정
        const newStatus: 'Y' | 'N' = targetAction === 'ALLOW' ? WITHDRAWAL_STATUS.ALLOWED as 'N' : WITHDRAWAL_STATUS.RESTRICTED as 'Y';
        const action = targetAction === 'ALLOW' ? WITHDRAWAL_STATUS_LABELS[WITHDRAWAL_STATUS.ALLOWED] : WITHDRAWAL_STATUS_LABELS[WITHDRAWAL_STATUS.RESTRICTED];

        // 확인창
        const confirmed = window.confirm(
            `${riderName}을(를) ${action} 하시겠습니까?\n${targetAction === 'RESTRICT' ? '출금제한하시면 해당 라이더는 더 이상 출금할 수 없습니다.' : '출금허용하시면 해당 라이더는 다시 출금할 수 있습니다.'}`
        );

        if (confirmed) {
            try {
                setLoading(true);
                await riderAccountingApi.updateWithdrawalStatus(riderId, suppId, newStatus);
                showToast(`${riderName} ${action}이 적용되었습니다.`, { type: ToastType.SUCCESS });
                await fetchRiderList(); // 목록 새로고침
            } catch {
                showToast(`${action} 설정에 실패했습니다.`, { type: ToastType.ERROR });
            } finally {
                setLoading(false);
            }
        }
    }, [showToast, fetchRiderList]);

    // 계약상태 코드 목록 조회
    const fetchContractStatusCodes = useCallback(async () => {
        try {
            setContractStatusLoading(true);
            const response = await riderAccountingApi.getContractStatusCodes();
            setContractStatusCodes(response || []);
        } catch (error) {
            setContractStatusCodes([]);
        } finally {
            setContractStatusLoading(false);
        }
    }, []);

    // 검색 조건 초기화
    const resetSearchConditions = useCallback(() => {
        setSearchRiderName('');
        setSearchRiderId('');
        setSearchContractStatus('');
        setSearchPartnerId('');
        setSearchWithdrawalStatus(''); // 출금상태 필터 초기화
    }, []);

    // 컴포넌트 마운트 시 초기화 및 데이터 조회
    useEffect(() => {
        const initialize = async () => {
            resetSearchConditions();
            fetchContractStatusCodes();

            if (userType === 'HEADQUARTERS') {
                // 본사 사용자: 협력사 목록 가져와서 첫 번째 협력사로 바로 검색
                try {
                    setLoading(true);
                    const names = await partnerAccountingApi.getPartnerNames();
                    setPartnerNames(names || []);

                    if (names && names.length > 0) {
                        const firstSupplier = names[0];
                        setSearchPartnerId(firstSupplier.suppId);

                        // 첫 번째 협력사로 바로 검색 실행
                        const params: Record<string, string> = {
                            suppId: firstSupplier.suppId
                        };
                        const response = await riderAccountingApi.searchRiders(params);

                        if (Array.isArray(response)) {
                            setAllData(response);
                            updatePageData(response, 1);
                        } else {
                            setAllData([]);
                            updatePageData([], 1);
                        }
                    }
                } catch (error) {
                    showToast('데이터 조회에 실패했습니다.', { type: ToastType.ERROR });
                    setAllData([]);
                    updatePageData([], 1);
                } finally {
                    setLoading(false);
                }
            } else {
                // 협력사 사용자: 기존 방식대로 조회
                fetchRiderList();
            }
        };

        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        // 상태
        searchRiderName,
        setSearchRiderName,
        searchRiderId,
        setSearchRiderId,
        searchContractStatus,
        setSearchContractStatus,
        searchPartnerId,
        setSearchPartnerId,
        searchWithdrawalStatus, // 출금상태 필터 추가
        setSearchWithdrawalStatus, // 출금상태 필터 setter 추가
        allData,
        riderData,
        loading,
        currentPage,
        totalPages,
        userType,
        contractStatusCodes,
        contractStatusLoading,
        partnerNames,
        itemsPerPage,

        // 함수
        handleSearch,
        handlePageChange,
        handleWithdrawalLimit,
        fetchRiderList,
        resetSearchConditions
    };
};
